'use client';

import React from 'react';
import { styled } from '@linaria/react';

import { clamp } from '@/utils';
import { BREAKPOINTS } from '@/constants';

import Demo from '@/components/Demo';
import SliderControl from '@/components/Controls/SliderControl';
import CodeSnippetProvider from '@/components/CodeSnippet/CodeSnippetProvider';
import {
  generateSnippet,
  getInitialValues,
} from './ViewboxSlidingDemo.helpers';

interface Props {
  variant?: 'default' | 'zooming';
  includeDragIndicator?: boolean;
  codeOutput: React.ReactNode;
}

const VIEWBOX_MIN_SIZE = 30;
const VIEWBOX_MAX_SIZE = 550;

function ViewboxSlidingDemo({
  variant = 'default',
  includeDragIndicator,
  codeOutput,
}: Props) {
  const [hasInteracted, setHasInteracted] = React.useState(false);
  const svgRef = React.useRef<SVGSVGElement>(null);

  const {
    x: initialX,
    y: initialY,
    size: initialSize,
  } = getInitialValues(variant);

  const [x, setX] = React.useState(initialX);
  const [y, setY] = React.useState(initialY);
  const [size, setSize] = React.useState(initialSize);

  const sizeRef = React.useRef(size);
  sizeRef.current = size;

  const [startPosition, setStartPosition] = React.useState<{
    x: number;
    y: number;
  } | null>(null);

  const id = React.useId().replace(/:/g, '');
  const gridId = `${id}-grid`;

  React.useEffect(() => {
    if (!svgRef.current || variant !== 'zooming') {
      return;
    }

    function handleWheel(event: WheelEvent) {
      const direction = Math.sign(event.deltaY);
      const currentSize = sizeRef.current;

      // So, we want to hijack the mousewheel event when the cursor is over this element, to zoom in/out. This can be annoying. To improve usability, we’ll see if they’re trying to zoom beyond the limit, and if so, we WON’T stop the page from scrolling.
      const isBeyondLimit =
        (direction === 1 && currentSize >= VIEWBOX_MAX_SIZE) ||
        (direction === -1 && currentSize <= VIEWBOX_MIN_SIZE);

      if (!isBeyondLimit) {
        event.preventDefault();
      }

      // The default zooming feels too quick
      const zoomScale = 0.5;
      const zoomAmount = event.deltaY * zoomScale;

      setSize((s) =>
        Math.round(
          clamp(s + zoomAmount, VIEWBOX_MIN_SIZE, VIEWBOX_MAX_SIZE)
        )
      );
    }

    const svgElem = svgRef.current;

    svgElem.addEventListener('wheel', handleWheel);

    return () => {
      svgElem.removeEventListener('wheel', handleWheel);
    };
  }, [variant]);

  React.useEffect(() => {
    if (!startPosition) {
      return;
    }

    function handlePointerMove(event: PointerEvent) {
      const { clientX, clientY } = event;
      event.preventDefault();

      if (!startPosition) {
        return;
      }

      const deltaX =
        (clientX - startPosition.x) / (300 / sizeRef.current);
      const deltaY =
        (clientY - startPosition.y) / (300 / sizeRef.current);

      setX(clamp(Math.round(x - deltaX), -350, 350));
      setY(clamp(Math.round(y - deltaY), -350, 350));
    }

    function handlePointerUp() {
      setStartPosition(null);
      document.body.style.cursor = '';
    }

    document.addEventListener('pointermove', handlePointerMove);
    document.addEventListener('pointerup', handlePointerUp);

    return () => {
      document.removeEventListener('pointermove', handlePointerMove);
      document.removeEventListener('pointerup', handlePointerUp);
    };
    // The current logic *requires* that `x` and `y` become stale. *Ideally* I should rewrite this, but it's kinda complicated and I’m lazy. As long as I don’t change anything, it works.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [startPosition]);

  const isDragging = startPosition !== null;

  const controls = [
    <SliderControl
      key="x"
      id="x"
      label="X"
      min={-350}
      max={350}
      value={x}
      setValue={setX}
      handleReset={() => setX(initialX)}
    />,
    <SliderControl
      key="y"
      id="y"
      label="Y"
      min={-350}
      max={350}
      value={y}
      setValue={setY}
      handleReset={() => setY(initialY)}
    />,
  ];

  if (variant === 'zooming') {
    controls.unshift(
      <SliderControl
        key="size"
        id="size"
        label="ViewBox Size"
        min={VIEWBOX_MIN_SIZE}
        max={VIEWBOX_MAX_SIZE}
        value={size}
        setValue={setSize}
        wrapperStyle={{
          gridColumn: '1 / 3',
        }}
        handleReset={() => setSize(300)}
      />
    );
  }

  return (
    <OuterWrapper>
      <Demo
        outputWrapper={null}
        initialValues={{
          x: 0,
          y: 0,
        }}
        controlPosition="bottom"
        controls={controls}
      >
        {() => {
          return (
            <Row>
              <CodeWrapper>
                <CodeSnippetProvider
                  lang="html"
                  code={generateSnippet(x, y, size)}
                >
                  {codeOutput}
                </CodeSnippetProvider>
              </CodeWrapper>
              <SvgWrapper>
                <Svg
                  ref={svgRef}
                  width="300"
                  height="300"
                  viewBox={`${x} ${y} ${size} ${size}`}
                  data-is-dragging={isDragging}
                  onPointerDown={(event) => {
                    event.preventDefault();
                    event.stopPropagation();

                    setHasInteracted(true);

                    document.body.style.cursor = 'grabbing';

                    setStartPosition({
                      x: event.clientX,
                      y: event.clientY,
                    });
                  }}
                >
                  <defs>
                    <pattern
                      id={gridId}
                      width="20"
                      height="20"
                      patternUnits="userSpaceOnUse"
                    >
                      <path
                        d={`
                          M 20 0 L 20 20
                          M 0 20 L 20 20
                        `}
                        fill="none"
                        stroke="hsl(0 0% 80%)"
                        strokeWidth="0.5"
                      />
                    </pattern>
                  </defs>
                  <rect
                    width="2000"
                    height="2000"
                    x="-600"
                    y="-600"
                    fill={`url(#${gridId})`}
                    fillOpacity="0.5"
                  />
                  <Axis x1="0" y1="-600" x2="0" y2="950" />
                  <Axis x1="-600" y1="0" x2="950" y2="0" />

                  {[
                    -300, -200, -100, 100, 200, 300, 400, 500, 600,
                    700, 800,
                  ].map((val) => {
                    return (
                      <React.Fragment key={val}>
                        <ThinLine
                          x1={val}
                          y1={-1600}
                          x2={val}
                          y2={1800}
                        />
                        <ThinLine
                          x1={-1600}
                          y1={val}
                          x2={1800}
                          y2={val}
                        />
                        <Label
                          x={val}
                          y={-12}
                          textAnchor="middle"
                          dominantBaseline="auto"
                        >
                          {val}
                        </Label>
                        <Peg x1={val} y1={0} x2={val} y2={-6} />
                        <Label
                          x={-12}
                          y={val}
                          textAnchor="end"
                          dominantBaseline="middle"
                        >
                          {val}
                        </Label>
                        <Peg x1={0} y1={val} x2={-6} y2={val} />
                      </React.Fragment>
                    );
                  })}

                  <MainRect x="0" y="0" width="200" height="200" />
                  <RectLabel
                    x="100"
                    y="100"
                    textAnchor="middle"
                    dominantBaseline="middle"
                    fill="white"
                  >
                    {'<rect>'}
                  </RectLabel>
                </Svg>
                {includeDragIndicator && (
                  <DragIndicator
                    src="/images/friendly-introduction-to-svgs/click-and-drag-outline.png"
                    data-has-interacted={hasInteracted}
                  />
                )}
              </SvgWrapper>
            </Row>
          );
        }}
      </Demo>
    </OuterWrapper>
  );
}

const OuterWrapper = styled.div`
  container-type: inline-size;
`;

const Row = styled.div`
  --gold: hsl(45deg 100% 50%);
  display: flex;
  gap: 16px;
  width: 100%;

  @container (max-width: 37rem) {
    flex-direction: column;
    align-items: center;
  }
`;

const CodeWrapper = styled.div`
  --inline-padding-override: 16px;
  --block-padding-override: 16px;
  --code-font-size: 0.875rem;
  --code-line-height: 1.25;
  flex: 1;
  min-width: 12.5rem;
  outline: 1px solid var(--color-gray-400);
  border-radius: 2px;
  white-space: pre;
  overflow: hidden;

  @container (max-width: 37rem) {
    width: 450px;
    max-width: 100%;
  }
  @container (max-width: 30rem) {
    white-space: pre-wrap;
  }
`;

const SvgWrapper = styled.div`
  flex: 0 0 300px;
  position: relative;
  width: 300px;
  height: 300px;

  &[data-variant='viewbox'] {
    max-width: 314px;
  }
`;

const Svg = styled.svg`
  display: block;
  outline: 1px solid var(--color-gray-300);
  cursor: grab;
  touch-action: none;

  &[data-is-dragging='true'] {
    cursor: grabbing;
  }

  text {
    user-select: none;
  }
`;

const RectLabel = styled.text`
  fill: var(--gold);
  font-weight: bold;
  font-family: var(--font-family-mono);
  font-size: 1.5rem;
`;

const Axis = styled.line`
  stroke: hsl(0deg 0% 70%);
  stroke-width: 3;
`;

const MainRect = styled.rect`
  fill: var(--gold);
  fill-opacity: 0.2;
  stroke: var(--gold);
  stroke-width: 3;
`;

const Label = styled.text`
  fill: white;
  font-weight: var(--font-weight-medium);
  font-family: var(--font-family-mono);
  font-size: 0.75rem;
`;

const Peg = styled.line`
  stroke: hsl(0deg 0% 70%);
  stroke-width: 3;
  stroke-linecap: round;
`;

const ThinLine = styled.line`
  stroke: hsl(0deg 0% 70%);
  stroke-width: 1;
  stroke-opacity: 0.25;
`;

const DragIndicator = styled.img`
  position: absolute;
  top: 0;
  right: 8px;
  width: 185px;
  transform: translate(100%, -30%);
  transition: opacity 1000ms;
  pointer-events: none;
  /* prettier-ignore */
  filter:
    drop-shadow(0 0 5px var(--color-background))
    drop-shadow(0 0 10px var(--color-background))
    drop-shadow(0 0 0px var(--color-background));

  @keyframes indicate {
    0% {
      transform: translate(100%, -30%);
    }
    60% {
      transform: translate(100%, -30%);
    }
    80% {
      transform: translate(90%, -30%);
    }
    100% {
      transform: translate(100%, -30%);
    }
  }

  @keyframes partialFade {
    from {
      opacity: 1;
    }
    to {
      opacity: 0.5;
    }
  }

  animation: partialFade 1000ms infinite alternate;

  &[data-has-interacted='true'] {
    animation:
      partialFade 1000ms infinite alternate,
      fadeToTransparent 500ms forwards;
  }

  @media (prefers-reduced-motion: no-preference) {
    animation: indicate 6000ms infinite
      cubic-bezier(0.761, 0, 0.243, 1);

    &[data-has-interacted='true'] {
      animation:
        indicate 6000ms infinite cubic-bezier(0.761, 0, 0.243, 1),
        fadeToTransparent 500ms forwards;
    }
  }

  @media (max-width: 1060px) {
    display: none;
  }

  html[data-color-mode='light'] & {
    /* prettier-ignore */
    filter:
      drop-shadow(0 0 5px white)
      drop-shadow(0 0 10px white)
      drop-shadow(0 0 0px white);
  }
`;

export default ViewboxSlidingDemo;
